import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { parseTextWithImages } from './parseTextWithImages';
import { ClockIcon, XIcon, CheckCircleIcon, BookmarkIcon, ArrowLeftIcon, ChevronRightIcon } from './icons/InterfaceIcons';
import { Logo } from './icons/Logo';
import { TestSummary } from './TestSummary';
import { MobileQuestionPalette } from './MobileQuestionPalette';

const getDifficultyFromLevel = (level) => {
    switch (level) {
        case 1: return 'Easy';
        case 2: return 'Medium';
        case 3: return 'Hard';
        default: return 'Medium';
    }
};

const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const QuestionPalette = ({ questions, statuses, currentIndex, navigateTo }) => {
    const [filter, setFilter] = useState('all');
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'answered': return 'bg-green-500 text-white';
            case 'notAnswered': return 'bg-red-500 text-white';
            case 'marked': return 'bg-purple-500 text-white';
            case 'answeredAndMarked': return 'bg-blue-500 text-white';
            default: return 'bg-brand-surface border border-brand-border text-brand-text-primary';
        }
    };

    const getFilteredQuestions = () => {
        return questions.map((_, index) => ({ index, status: statuses.get(index) }))
            .filter(({ status }) => {
                if (filter === 'all') return true;
                if (filter === 'answered') return status === 'answered' || status === 'answeredAndMarked';
                if (filter === 'unanswered') return status === 'notAnswered' || status === 'marked';
                if (filter === 'marked') return status === 'marked' || status === 'answeredAndMarked';
                return true;
            });
    };

    const filteredQuestions = getFilteredQuestions();
    const stats = {
        answered: questions.filter((_, i) => ['answered', 'answeredAndMarked'].includes(statuses.get(i))).length,
        unanswered: questions.filter((_, i) => ['notAnswered', 'marked'].includes(statuses.get(i))).length,
        marked: questions.filter((_, i) => ['marked', 'answeredAndMarked'].includes(statuses.get(i))).length
    };

    return (
        <div className="bg-brand-surface p-4 rounded-lg border border-brand-border">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">Question Palette</h3>
                <div className="text-xs text-brand-text-secondary">
                    {currentIndex + 1}/{questions.length}
                </div>
            </div>
            
            {/* Filter buttons */}
            <div className="mb-4 flex flex-wrap gap-1">
                {[
                    { key: 'all', label: 'All', count: questions.length },
                    { key: 'answered', label: 'Answered', count: stats.answered },
                    { key: 'unanswered', label: 'Unanswered', count: stats.unanswered },
                    { key: 'marked', label: 'Marked', count: stats.marked }
                ].map(({ key, label, count }) => (
                    <button
                        key={key}
                        onClick={() => setFilter(key)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                            filter === key 
                                ? 'bg-brand-primary text-white' 
                                : 'bg-brand-bg text-brand-text-secondary hover:bg-brand-primary/10'
                        }`}
                    >
                        {label} ({count})
                    </button>
                ))}
            </div>
            
            <div className="grid grid-cols-5 gap-2 max-h-64 overflow-y-auto">
                {filteredQuestions.map(({ index }) => (
                    <button
                        key={index}
                        onClick={() => navigateTo(index)}
                        className={`w-10 h-10 flex items-center justify-center rounded font-semibold transition-all ${
                            getStatusColor(statuses.get(index))
                        } ${
                            currentIndex === index ? 'ring-2 ring-brand-primary ring-offset-2' : ''
                        }`}
                    >
                        {index + 1}
                    </button>
                ))}
            </div>
            
            <div className="mt-4 space-y-2 text-xs text-brand-text-secondary">
                <div className="flex items-center"><span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>Answered</div>
                <div className="flex items-center"><span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>Not Answered</div>
                <div className="flex items-center"><span className="w-3 h-3 rounded-full bg-purple-500 mr-2"></span>Marked</div>
                <div className="flex items-center"><span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>Answered & Marked</div>
            </div>
        </div>
    );
};

export const TestScreen = ({ test, user, onSubmitTest, onBack, forceSubmit = false }) => {
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(() => {
        const savedIndex = sessionStorage.getItem(`test-${test.id}-currentIndex`);
        return savedIndex ? parseInt(savedIndex, 10) : 0;
    });
    const [answers, setAnswers] = useState(() => {
        const savedAnswers = sessionStorage.getItem(`test-${test.id}-answers`);
        return savedAnswers ? new Map(JSON.parse(savedAnswers)) : new Map();
    });
    const [statuses, setStatuses] = useState(() => {
        const savedStatuses = sessionStorage.getItem(`test-${test.id}-statuses`);
        return savedStatuses ? new Map(JSON.parse(savedStatuses)) : new Map();
    });
    const [timeLeft, setTimeLeft] = useState(() => {
        const savedTimeLeft = sessionStorage.getItem(`test-${test.id}-timeLeft`);
        return savedTimeLeft ? parseInt(savedTimeLeft, 10) : test.duration * 60;
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [showMobilePalette, setShowMobilePalette] = useState(false);
    const [questionTimes, setQuestionTimes] = useState<Map<number, number>>(() => {
        const savedTimes = sessionStorage.getItem(`test-${test.id}-questionTimes`);
        if (savedTimes) {
            try {
                return new Map(JSON.parse(savedTimes));
            } catch (e) {
                console.error("Error parsing questionTimes", e);
            }
        }
        return new Map();
    });
    const lastTimeRef = useRef(Date.now());

    useEffect(() => {
        sessionStorage.setItem(`test-${test.id}-currentIndex`, currentIndex.toString());
    }, [currentIndex, test.id]);

    useEffect(() => {
        sessionStorage.setItem(`test-${test.id}-answers`, JSON.stringify(Array.from(answers.entries())));
    }, [answers, test.id]);

    useEffect(() => {
        sessionStorage.setItem(`test-${test.id}-statuses`, JSON.stringify(Array.from(statuses.entries())));
    }, [statuses, test.id]);

    useEffect(() => {
        sessionStorage.setItem(`test-${test.id}-timeLeft`, timeLeft.toString());
    }, [timeLeft, test.id]);

    useEffect(() => {
        sessionStorage.setItem(`test-${test.id}-questionTimes`, JSON.stringify(Array.from(questionTimes.entries())));
    }, [questionTimes, test.id]);

    // Update question timing when index changes
    useEffect(() => {
        const now = Date.now();
        const diff = Math.floor((now - lastTimeRef.current) / 1000);
        
        // We only care about the transition, so we update the PREVIOUS question index time
        // But since useEffect runs AFTER the change, we need to know the OLD index.
        // Wait, a better way is to update the current question time in the interval or on navigation.
        
        lastTimeRef.current = now;
    }, [currentIndex]);

    const handleSubmit = useCallback(() => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const results = {
            correct: 0,
            incorrect: 0,
            unattempted: 0,
            score: 0,
            answers: Object.fromEntries(answers),
            timeTaken: (test.duration * 60) - timeLeft,
            accuracy: 0,
            questionTimes: Object.fromEntries(questionTimes),
        };

        questions.forEach((q, index) => {
            const userAnswer = answers.get(index);
            if (userAnswer) {
                const isCorrect = q.type === 'Integer'
                    ? userAnswer.trim() === q.answer.trim()
                    : userAnswer.trim().toLowerCase() === q.answer.trim().toLowerCase();

                if (isCorrect) {
                    results.correct++;
                } else {
                    results.incorrect++;
                }
            } else {
                results.unattempted++;
            }
        });

        results.score = Math.max(0, (results.correct * 4) - (results.incorrect * (test.negativeMarks || 1)));
        const totalQuestions = questions.length;
        const attemptedQuestions = totalQuestions - results.unattempted;
        results.accuracy = attemptedQuestions > 0 ? Math.round((results.correct / attemptedQuestions) * 100) : 0;
        
        sessionStorage.removeItem(`test-${test.id}-answers`);
        sessionStorage.removeItem(`test-${test.id}-statuses`);
        sessionStorage.removeItem(`test-${test.id}-currentIndex`);
        sessionStorage.removeItem(`test-${test.id}-timeLeft`);
        sessionStorage.removeItem('currentTest');

        onSubmitTest({ ...results, totalQuestions, questions });
    }, [isSubmitting, answers, questions, timeLeft, test.duration, test.negativeMarks, onSubmitTest]);

    useEffect(() => {
        if (forceSubmit) {
            handleSubmit();
        }
    }, [forceSubmit, handleSubmit]);

    useEffect(() => {
        const savedStatusesString = sessionStorage.getItem(`test-${test.id}-statuses`);
        let initialStatuses = new Map();
        if (savedStatusesString) {
            initialStatuses = new Map(JSON.parse(savedStatusesString));
        }

        if (test && test.questions) {
            // Custom test flow
            setQuestions(test.questions);
            // Ensure all questions have a status, default to 'notAnswered' if not saved
            test.questions.forEach((_, index) => {
                if (!initialStatuses.has(index)) {
                    initialStatuses.set(index, 'notAnswered');
                }
            });
            setStatuses(initialStatuses);
            setIsLoading(false);
        } else {
            // Mock test flow
            const fetchTestQuestions = async () => {
                setIsLoading(true);
                try {
                    const { data: examData, error: examError } = await supabase
                        .from('exams').select('Questions').eq('id', test.id).single();
                    if (examError || !examData?.Questions) throw new Error("Could not find test details.");

                    const questionIds = examData.Questions.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id) && id > 0);
                    
                    let questionSourceTable = 'nda';
                    if (test.exam) {
                        const tagsLower = test.exam;
                        if (tagsLower.toUpperCase() === "JEE-MAIN") questionSourceTable = 'jeemain';
                        else if (tagsLower.toUpperCase() === "NDA") questionSourceTable = 'nda';
                        else if (tagsLower.toUpperCase() === "NEET") questionSourceTable = 'neet';
                        else questionSourceTable = 'questions'
                    }

                    const { data: questionData, error: questionError } = await supabase
                        .from(questionSourceTable).select('*').in('id', questionIds);

                    if (questionError) throw questionError;
                    
                    const formattedQuestions = questionData.map(q => {
                        let answer = '';
                        if (q.type === 'Integer') {
                            answer = q.correct?.toString() || q.correct_option?.toString() || '';
                        } else {
                            // Single Choice mapping
                            const correctVal = q.correct || q.correct_option;
                            if (correctVal === 'option1' || correctVal === 'option2' || correctVal === 'option3' || correctVal === 'option4') {
                                answer = q[correctVal];
                            } else if (correctVal === 'A' || correctVal === 'B' || correctVal === 'C' || correctVal === 'D') {
                                const map = { 'A': q.option1, 'B': q.option2, 'C': q.option3, 'D': q.option4 };
                                answer = map[correctVal];
                            } else {
                                answer = correctVal; // Literal answer
                            }
                        }

                        return {
                            id: q.id.toString(), 
                            text: q.question, 
                            options: q.type === 'Integer' ? [] : [q.option1, q.option2, q.option3, q.option4].filter(opt => opt),
                            answer: answer, 
                            solution: q.explanation || q.solution || '', 
                            subject: q.subject || q.Subject || 'General', 
                            topic: q.tags || q.chapter || q.topic || 'General',
                            difficulty: getDifficultyFromLevel(q.level),
                            type: q.type || 'Single Choice',
                            sourceTable: questionSourceTable
                        };
                    });
                    
                    const questionMap = new Map(formattedQuestions.map(q => [q.id, q]));
                    const orderedQuestions = questionIds.map(id => questionMap.get(id.toString())).filter(Boolean);

                    setQuestions(orderedQuestions);
                    // Ensure all questions have a status, default to 'notAnswered' if not saved
                    orderedQuestions.forEach((_, index) => {
                        if (!initialStatuses.has(index)) {
                            initialStatuses.set(index, 'notAnswered');
                        }
                    });
                    setStatuses(initialStatuses);
                } catch (err) {
                    setError(err.message);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchTestQuestions();
        }
    }, [test]);
    
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [handleSubmit]);

    const updateStatus = (index, answer, isMarked) => {
        const currentStatus = statuses.get(index);
        let newStatus;
        if (answer) {
            newStatus = isMarked || currentStatus === 'marked' || currentStatus === 'answeredAndMarked' ? 'answeredAndMarked' : 'answered';
        } else {
            newStatus = isMarked || currentStatus === 'marked' || currentStatus === 'answeredAndMarked' ? 'marked' : 'notAnswered';
        }
        setStatuses(new Map(statuses.set(index, newStatus)));
    };

    const handleSelectOption = (option) => {
        setAnswers(new Map(answers.set(currentIndex, option)));
        updateStatus(currentIndex, option, false);
    };

    const handleClearResponse = () => {
        const newAnswers = new Map(answers);
        newAnswers.delete(currentIndex);
        setAnswers(newAnswers);
        updateStatus(currentIndex, null, false);
    };

    const handleMarkForReview = () => {
        updateStatus(currentIndex, answers.get(currentIndex), true);
        handleNext();
    };

    const handleNext = () => {
        const now = Date.now();
        const diff = Math.floor((now - lastTimeRef.current) / 1000);
        const newTimes = new Map<number, number>(questionTimes);
        const currentTime = newTimes.get(currentIndex) || 0;
        newTimes.set(currentIndex, currentTime + diff);
        setQuestionTimes(newTimes);
        lastTimeRef.current = now;

        if (currentIndex < questions.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };
    
    const handlePrev = () => {
        const now = Date.now();
        const diff = Math.floor((now - lastTimeRef.current) / 1000);
        const newTimes = new Map<number, number>(questionTimes);
        const currentTime = newTimes.get(currentIndex) || 0;
        newTimes.set(currentIndex, currentTime + diff);
        setQuestionTimes(newTimes);
        lastTimeRef.current = now;

        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleNavigateTo = (index) => {
        const now = Date.now();
        const diff = Math.floor((now - lastTimeRef.current) / 1000);
        const newTimes = new Map<number, number>(questionTimes);
        const currentTime = newTimes.get(currentIndex) || 0;
        newTimes.set(currentIndex, currentTime + diff);
        setQuestionTimes(newTimes);
        lastTimeRef.current = now;
        
        setCurrentIndex(index);
    };
    
    const handleSaveAndNext = () => {
        handleNext();
    };

    if (isLoading) return <div className="flex items-center justify-center h-screen"><div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div></div>;
    if (error) return <div className="flex items-center justify-center h-screen text-red-500">Error: {error}</div>;

    const currentQuestion = questions[currentIndex];

    return (
        <div className="flex flex-col h-screen bg-brand-bg text-brand-text-primary">
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" aria-modal="true">
                    <div className="bg-brand-surface rounded-lg shadow-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold">Confirm Submission</h2>
                        <p className="text-brand-text-secondary mt-2">Are you sure you want to submit the test? You cannot make any changes after this.</p>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button onClick={() => setShowConfirmModal(false)} className="px-4 py-2 text-sm font-semibold rounded-lg border border-brand-border hover:bg-gray-100">Cancel</button>
                            <button onClick={handleSubmit} disabled={isSubmitting} className="px-4 py-2 text-sm font-semibold text-white bg-brand-accent-red hover:bg-red-700 disabled:opacity-50">{isSubmitting ? 'Submitting...' : 'Submit Test'}</button>
                        </div>
                    </div>
                </div>
            )}
            
            {showSummary && (
                <TestSummary
                    questions={questions}
                    statuses={statuses}
                    answers={answers}
                    timeLeft={timeLeft}
                    onClose={() => setShowSummary(false)}
                    onSubmit={() => {
                        setShowSummary(false);
                        setShowConfirmModal(true);
                    }}
                    onNavigateToQuestion={(index) => {
                        setCurrentIndex(index);
                        setShowSummary(false);
                    }}
                />
            )}
            
            {showMobilePalette && (
                <MobileQuestionPalette
                    questions={questions}
                    statuses={statuses}
                    currentIndex={currentIndex}
                    navigateTo={setCurrentIndex}
                    onClose={() => setShowMobilePalette(false)}
                />
            )}
            
            <header className="flex items-center justify-between p-4 border-b border-brand-border bg-brand-surface flex-shrink-0 shadow-sm">
                <div className="flex items-center space-x-4">
                    <Logo size="small" />
                    <div className="hidden sm:block">
                        <h2 className="text-lg font-bold text-brand-text-primary">{test.name}</h2>
                        <p className="text-xs text-brand-text-secondary">{questions.length} Questions • {test.duration} Minutes</p>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-mono text-lg ${
                        timeLeft <= 300 ? 'bg-red-100 text-red-700 border border-red-300' : 
                        timeLeft <= 900 ? 'bg-yellow-100 text-yellow-700 border border-yellow-300' :
                        'bg-brand-bg text-brand-text-primary border border-brand-border'
                    }`}>
                        <ClockIcon className="w-5 h-5"/>
                        <span>{formatTime(timeLeft)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button 
                            onClick={() => setShowMobilePalette(true)}
                            className="lg:hidden px-3 py-2 text-sm font-semibold border border-brand-border hover:bg-brand-surface rounded-lg transition-colors"
                        >
                            Questions
                        </button>
                        <button 
                            onClick={() => setShowSummary(true)}
                            className="hidden sm:block px-4 py-2 text-sm font-semibold border border-brand-border hover:bg-brand-surface rounded-lg transition-colors"
                        >
                            Summary
                        </button>
                        <button 
                            onClick={() => setShowConfirmModal(true)} 
                            className="px-4 py-2 text-sm font-semibold text-white bg-brand-accent-red hover:bg-red-700 rounded-lg transition-colors shadow-sm"
                        >
                            Submit Test
                        </button>
                    </div>
                </div>
            </header>
            <div className="flex-1 flex overflow-hidden">
                <main className="flex-1 p-6 overflow-y-auto">
                    {currentQuestion && (
                        <div className="max-w-4xl mx-auto">
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xl font-semibold text-brand-text-primary">
                                        Question {currentIndex + 1} of {questions.length}
                                    </h3>
                                    {currentQuestion.subject && (
                                        <span className="px-3 py-1 text-xs font-medium bg-brand-primary/10 text-brand-primary rounded-full">
                                            {currentQuestion.subject}
                                        </span>
                                    )}
                                </div>
                                <div className="bg-brand-surface border border-brand-border rounded-lg p-6">
                                    <div className="text-brand-text-primary text-lg leading-relaxed">
                                        {parseTextWithImages(currentQuestion.text)}
                                    </div>
                                </div>
                            </div>
                            
                            {currentQuestion.type === 'Integer' ? (
                                <div className="bg-brand-surface border border-brand-border rounded-lg p-6">
                                    <label className="block text-sm font-medium text-brand-text-primary mb-2">
                                        Enter your numerical answer:
                                    </label>
                                    <input
                                        type="text"
                                        value={answers.get(currentIndex) || ''}
                                        onChange={(e) => handleSelectOption(e.target.value)}
                                        className="w-full px-4 py-3 text-lg border-2 border-brand-border rounded-lg focus:border-brand-primary focus:outline-none bg-brand-bg text-brand-text-primary"
                                        placeholder="Enter integer value (e.g., 42)"
                                    />
                                    <p className="text-xs text-brand-text-secondary mt-2">
                                        Note: Enter only the numerical value without units
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {currentQuestion.options.map((option, index) => {
                                        const isSelected = answers.get(currentIndex) === option;
                                        return (
                                            <div
                                                key={index}
                                                className={`relative w-full flex items-start text-left p-4 border-2 rounded-lg transition-all duration-200 ${
                                                    isSelected 
                                                        ? 'border-brand-primary bg-brand-primary/10 shadow-md' 
                                                        : 'border-brand-border hover:border-brand-primary/50 hover:bg-brand-surface'
                                                }`}
                                            >
                                                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mr-4 flex-shrink-0 ${
                                                    isSelected 
                                                        ? 'border-brand-primary bg-brand-primary text-white' 
                                                        : 'border-brand-border'
                                                }`}>
                                                    <span className="font-semibold text-sm">{String.fromCharCode(65 + index)}</span>
                                                </div>
                                                <div className="flex-1 pt-1">{parseTextWithImages(option)}</div>
                                                {/* Clickable Overlay */}
                                                <div 
                                                    className="absolute inset-0 cursor-pointer z-10"
                                                    onClick={() => handleSelectOption(option)}
                                                ></div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </main>
                <aside className="hidden lg:block w-80 p-6 border-l border-brand-border overflow-y-auto flex-shrink-0">
                    <QuestionPalette questions={questions} statuses={statuses} currentIndex={currentIndex} navigateTo={handleNavigateTo} />
                </aside>
            </div>
            <footer className="flex items-center justify-between p-4 border-t border-brand-border bg-brand-surface flex-shrink-0 shadow-sm">
                <button 
                    onClick={handlePrev} 
                    disabled={currentIndex === 0} 
                    className="flex items-center space-x-2 px-4 py-2 font-semibold rounded-lg border border-brand-border hover:bg-brand-bg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <ArrowLeftIcon className="w-4 h-4"/>
                    <span className="hidden sm:inline">Previous</span>
                </button>
                
                <div className="flex flex-wrap items-center gap-2">
                    <button 
                        onClick={handleClearResponse} 
                        className="flex items-center space-x-2 px-3 py-2 text-sm font-semibold rounded-lg bg-brand-accent-amber/20 text-brand-accent-amber hover:bg-brand-accent-amber/30 transition-colors"
                    >
                        <XIcon className="w-4 h-4"/>
                        <span className="hidden sm:inline">Clear</span>
                    </button>
                    
                    <button 
                        onClick={handleMarkForReview} 
                        className="flex items-center space-x-2 px-3 py-2 text-sm font-semibold rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                    >
                        <BookmarkIcon className="w-4 h-4"/>
                        <span className="hidden sm:inline">Mark & Next</span>
                    </button>
                    
                    {answers.get(currentIndex) && (
                        <button 
                            onClick={handleSaveAndNext} 
                            className="flex items-center space-x-2 px-3 py-2 text-sm font-semibold rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                        >
                            <CheckCircleIcon className="w-4 h-4"/>
                            <span className="hidden sm:inline">Save & Next</span>
                        </button>
                    )}
                </div>
                
                <button 
                    onClick={handleNext} 
                    disabled={currentIndex === questions.length - 1} 
                    className="flex items-center space-x-2 px-4 py-2 font-semibold rounded-lg bg-brand-primary text-white hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRightIcon className="w-4 h-4"/>
                </button>
            </footer>
        </div>
    );
};